import { Content, ContentNode, ContentNodeType } from '@amzn/awscat-pattern-library-service-model';
import { TabsProps } from '@amzn/awsui-components-core';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { findNodeByHref, findParentNode } from '../../../api/ContentUtil';
import JSONSerializer from '../../../common/JSONSerializer';
import BrowserHistory from '../../utils/BrowserHistory';
import { Location, UnregisterCallback } from 'history';
import * as UtilApi from '../../../api/UtilsApi';

import { ContentDataProps } from './ContentData';
import { IMplServiceClient } from '../../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';

/**
 * Shared state interface for both readonly and edit views.
 * Extended by ReadonlyContentDataState and EditContentDataState.
 */
export interface BaseContentDataState {
  activeTabId: string; // Currently selected tab (e.g. 'content', 'details', 'history')
  currentPage: ContentNode; // The currently displayed page (for multi-page content like Guides)
  expanded: boolean; // Whether content sections are expanded or collapsed
  isLoading?: boolean;
  location?: Location; // Current browser location (for hash-based navigation)
  updatedContent: Content; // Working copy of the content being viewed/edited
  viewChange?: boolean; // Whether track-changes diff view is active
  checkedHasDiff?: boolean;
}

/**
 * SHARED UTILITY BASE CLASS for both ReadonlyContentData and EditContentData.
 *
 * This is NOT a Template Method class — it's a utility base that provides common
 * functionality needed by both readonly and edit views:
 *
 * - Content cloning (deep copy via JSON serialization)
 * - Browser history listening (react to URL hash changes for in-page navigation)
 * - Hash-based page navigation (scroll to section when URL hash changes)
 * - Help panel integration (show section navigation in the right panel)
 * - Epic ID migration (replace title-based IDs with UUIDs — legacy data fix)
 * - Content update handling (sync state when props change)
 * - Tab change handling
 *
 * Inheritance tree:
 *   BaseContentData (THIS)
 *     ├── ReadonlyContentData → DraftReadonlyContentData
 *     └── EditContentData → DraftEditContentData
 *
 * Generic type parameter T allows subclasses to extend BaseContentDataState
 * with their own state fields.
 */
class BaseContentData<T extends BaseContentDataState> extends React.Component<ContentDataProps, T> {
  // JSON serializer used for deep-cloning content objects
  protected readonly serializer: JSONSerializer;
  // Unsubscribe function for browser history listener (called in componentWillUnmount)
  // @ts-expect-error TS2564 - strict migration
  protected historyUnlisten: UnregisterCallback;
  // Backend API client for fetching content, workflows, etc.
  protected readonly mplService: IMplServiceClient;
  // Whether the current user is in edit mode (set by subclasses)
  // @ts-expect-error TS2564 - strict migration
  protected isEditingEnabled: boolean;

  constructor(props: ContentDataProps) {
    super(props);
    this.serializer = new JSONSerializer();
    this.mplService = MplServiceClientFactory.create();
  }

  // Deep-clone the content prop to create a mutable working copy.
  // Uses JSON serialize/deserialize to ensure a complete deep copy with proper
  // class instances (not just plain objects) via the Content class deserializer.
  protected cloneContent = () => {
    const { content } = this.props;
    return content
      ? this.serializer.deserialize<Content>(this.serializer.stringify(content), Content)
      : UtilApi.getEmptyContent();
  };

  // Listen for browser history changes to handle in-page navigation via URL hash.
  // When the URL hash changes (e.g. #architecture → #epics), scroll to that section.
  // Only reacts to changes on content viewer paths (not other pages).
  protected listenBrowserHistory = () => {
    const { content } = this.props;
    return BrowserHistory.listen((location: Location) => {
      const { location: currentLocation } = this.state;
      if (
        location.pathname.startsWith('/content/') ||
        location.pathname.startsWith('/content-viewer/') ||
        location.pathname.startsWith('/content-update-viewer/') ||
        location.pathname.startsWith('/content-finalizing-viewer')
      ) {
        // Same page, different hash → navigate to section
        if (
          currentLocation?.pathname === location.pathname &&
          currentLocation?.hash !== location.hash
        ) {
          this.updateCurrentPageByHashTag(location.hash, content);
        } else {
          this.setState({
            location,
          });
        }
      }
    });
  };

  // Navigate to a content section based on URL hash.
  // For multi-page content (Guides/Strategies), this also switches the current page
  // if the target section is on a different page.
  // For sections (not pages), it finds the parent page and sets that as current.
  protected updateCurrentPageByHashTag = (hashTag: string, content: Content) => {
    const hashId = hashTag.slice(1); // Remove the '#' prefix
    const node = hashId ? findNodeByHref(hashId, [content.contentData]) : content.contentData;
    if (node) {
      let currentPage = node;
      // If the target is a section (not a page), navigate to its parent page instead
      // and let the section scroll into view within that page.
      if (node.nodeType === ContentNodeType.Section) {
        // @ts-expect-error TS2345 - strict migration
        const parrentNode = findParentNode(node.id, this.props.content.contentData, null);
        currentPage = parrentNode;
      }

      this.setState({ currentPage });
      this.showContentSectionNavigation(currentPage);
      // Scroll the target element into view
      const anchor = document.getElementById(hashId);
      anchor?.scrollIntoView();
    }
  };

  // Show the content section navigation in the help panel (right sidebar).
  // In readonly mode, displays a list of sections for the current page.
  // In edit mode, the help panel is hidden (editor has its own navigation).
  protected showContentSectionNavigation = (page: ContentNode, closeHelpPanel: boolean = false) => {
    if (!this.isEditingEnabled && page?.contentSections?.length) {
      // @ts-expect-error TS2722 - strict migration
      this.props.helpPanelContext.panelHandler('content-page-sections')(page);
      if (closeHelpPanel) {
        this.props.helpPanelContext.panelClose();
      }
    } else {
      this.props.helpPanelContext.panelHidden();
    }
  };

  // LEGACY DATA FIX: Replace epic IDs that use the epic title as the ID with proper UUIDs.
  // Old content used the epic title as the ID, which breaks when titles are edited.
  // This migration runs on every load to fix any remaining legacy data.
  protected updateEpicIds = (content: Content) => {
    const epics =
      content.contentData &&
      content.contentData.contentSections &&
      content.contentData.contentSections.find((section) => section.title === 'Epics');
    if (epics && Array.isArray(epics.contentData)) {
      epics.contentData.forEach((e) => {
        if (e.id === e.title) {
          e.id = uuid();
        }
      });
    }
  };

  // Handle content prop changes from the parent (ContentViewer).
  // Deep-clones the new content, finds the currently displayed page in the new
  // content tree, and updates state. This keeps the view in sync when the parent
  // re-fetches data (e.g. after another user's edit is detected).
  protected handleContentUpdate = () => {
    const updatedContent = this.serializer.deserialize<Content>(
      this.serializer.stringify(this.props.content),
      Content,
    );
    const { contentData } = updatedContent;
    const { subContent } = contentData;
    let currentPage = contentData;

    // Try to stay on the same page after the update
    if (this.state.currentPage.id !== 'rootId') {
      // @ts-expect-error TS2322 - strict migration
      currentPage = subContent.find((page) => page.id === this.state.currentPage.id);
    }

    this.setState({
      currentPage,
      updatedContent,
    });
    this.showContentSectionNavigation(currentPage);
  };

  // Handle tab changes (e.g. Latest → History → Update Requests in readonly,
  // or Details → Content → Code in edit mode).
  protected handleTabChange = (event: CustomEvent<TabsProps.ChangeDetail>) => {
    const { activeTabId } = event.detail;
    this.changeTabView(activeTabId);
  };

  // Programmatically switch tabs. Also clears any feedback hash from the URL
  // (feedback deep links use #feedback-{id} which should be cleared when navigating away).
  protected changeTabView = (tabId: string) => {
    this.setState({
      activeTabId: tabId,
    });
    // Clear feedback hash when switching tabs
    const currentHash = window.location.hash.slice(1);
    if (currentHash.startsWith('feedback-')) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (this.props.onTabViewChange) {
      this.props.onTabViewChange(tabId);
    }
  };
}

export default BaseContentData;
